import { Check, Minus, Upload, X } from 'lucide-react'
import { useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate, useSearchParams } from 'react-router'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { TableSkeletonRows } from '@/components/ui/table-skeleton'
import { CoiGeneratorItem, CoiGeneratorUser, useCoiGeneratorList } from '../../../../models/coiGenerator'

function formatUserName(user: CoiGeneratorUser | null): string {
  if (!user) return '\u2014'
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ')
  return name || user.email
}
import { UploadPolicyDialog } from './UploadPolicyDialog'

/** Policy files beyond the one named in the row. 0 when the backend predates the field. */
function extraPolicyFiles(item: CoiGeneratorItem): number {
  return Math.max((item.policyFileCount ?? 1) - 1, 0)
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function CoiGenerator() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [uploadOpen, setUploadOpen] = useState(false)

  const page = Number(searchParams.get('page') || '1')

  const { items, count, hasNextPage, hasPreviousPage, isLoading } = useCoiGeneratorList({ page })

  function goToPage(p: number) {
    if (p > 1) {
      setSearchParams({ page: String(p) })
    } else {
      setSearchParams({})
    }
  }

  return (
    <>
      <Helmet>
        <title>COI Generator</title>
      </Helmet>
      <div className="flex flex-col gap-5">
        <div className="mb-2 text-2xl font-semibold">COI Generator</div>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Test Policy Documents</CardTitle>
              <Button onClick={() => setUploadOpen(true)}>
                <Upload className="mr-2 size-4" />
                Upload Policy
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-semibold">Policy File</TableHead>
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="font-semibold">COI Generated</TableHead>
                  <TableHead className="font-semibold">Correct</TableHead>
                  <TableHead className="font-semibold">Created by</TableHead>
                  <TableHead className="font-semibold">Reviewed by</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && <TableSkeletonRows columns={6} />}
                {!isLoading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="pt-5 text-center">
                      No documents found
                    </TableCell>
                  </TableRow>
                )}
                {items.map((item) => (
                  <TableRow
                    key={item.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/insure/internal/coi-generator/${item.id}`)}
                  >
                    <TableCell className="font-medium">
                      {item.policyFileName || '\u2014'}
                      {extraPolicyFiles(item) > 0 && (
                        <span className="text-muted-foreground font-normal">
                          {' '}
                          +{extraPolicyFiles(item)} more
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(item.dateCreated)}</TableCell>
                    <TableCell>
                      {item.hasCoi ? (
                        <Check className="size-4 text-green-600" />
                      ) : (
                        <X className="size-4 text-neutral-400" />
                      )}
                    </TableCell>
                    <TableCell>
                      {item.isCorrect === true ? (
                        <Check className="size-4 text-green-600" />
                      ) : item.isCorrect === false ? (
                        <X className="size-4 text-red-500" />
                      ) : (
                        <Minus className="size-4 text-neutral-400" />
                      )}
                    </TableCell>
                    <TableCell>{formatUserName(item.createdBy)}</TableCell>
                    <TableCell>{formatUserName(item.reviewedBy)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {(hasPreviousPage || hasNextPage) && (
              <div className="mt-4 flex items-center justify-between">
                <div className="text-muted-foreground text-base">
                  Page {page} ({count} total)
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!hasPreviousPage}
                    onClick={() => goToPage(page - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!hasNextPage}
                    onClick={() => goToPage(page + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <UploadPolicyDialog open={uploadOpen} onOpenChange={setUploadOpen} />
    </>
  )
}
